import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, MessageSquareText } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { StarRating, StarRatingInput } from "@/components/StarRating";

export function CourseReviewsPanel({ courseId, canReview, myReview }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(myReview?.rating || 0);
  const [comment, setComment] = useState(myReview?.comment || "");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/courses/${courseId}/reviews`).then((res) => setData(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (rating < 1) { toast.error("Please pick a star rating"); return; }
    setSubmitting(true);
    try {
      await api.post(`/courses/${courseId}/reviews`, { rating, comment });
      toast.success("Review submitted!");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to submit review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-500" /></div>;

  return (
    <div data-testid="course-reviews-panel">
      <div className="flex items-center gap-2 mb-4">
        {data.avg_rating != null ? (
          <>
            <StarRating rating={data.avg_rating} size="w-4 h-4" />
            <span className="text-sm font-semibold text-white">{data.avg_rating}</span>
            <span className="text-xs text-slate-500">({data.review_count} review{data.review_count === 1 ? "" : "s"})</span>
          </>
        ) : (
          <span className="text-xs text-slate-500" data-testid="course-reviews-empty-rating">No reviews yet — be the first!</span>
        )}
      </div>

      {canReview && (
        <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 mb-5">
          <p className="text-xs font-semibold text-slate-300 mb-2">{myReview ? "Update your review" : "Rate this course"}</p>
          <StarRatingInput value={rating} onChange={setRating} testId="course-review-star-input" />
          <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="Share your experience (optional)"
            data-testid="course-review-comment-input"
            className="w-full mt-3 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 resize-none" />
          <button onClick={submit} disabled={submitting} data-testid="course-review-submit-btn"
            className="mt-3 flex items-center gap-2 bg-primary hover:bg-primary/90 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {myReview ? "Update Review" : "Submit Review"}
          </button>
        </div>
      )}

      {data.reviews.length === 0 ? (
        <p className="text-xs text-slate-500 text-center py-4" data-testid="course-reviews-list-empty">
          <MessageSquareText className="w-6 h-6 text-slate-600 mx-auto mb-2" /> No reviews yet.
        </p>
      ) : (
        <div className="space-y-3">
          {data.reviews.map((r) => (
            <div key={r.id} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3.5" data-testid={`course-review-item-${r.id}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-semibold text-white">{r.student_name}</span>
                <StarRating rating={r.rating} />
              </div>
              {r.comment && <p className="text-xs text-slate-400 mt-1">{r.comment}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
